import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShoppingCart } from "lucide-react";
import { Input } from "../components/ui/input";
import { useVendorAuth, vendorFetch } from "./VendorAuthContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

const STATUS_CLR = {
  Approved: "bg-emerald-100 text-emerald-700",
  Sent: "bg-sky-100 text-sky-700",
  "Partially Received": "bg-amber-100 text-amber-700",
  Closed: "bg-violet-100 text-violet-700",
  Cancelled: "bg-red-100 text-red-700",
};

export default function VendorPOList() {
  const { token } = useVendorAuth();
  const [pos, setPOs] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    vendorFetch(token)("/api/vendor/purchase-orders")
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("Failed to load")))
      .then(setPOs)
      .catch(() => setPOs([]));
  }, [token]);

  const filtered = useMemo(() => (pos || []).filter((p) => {
    if (status && p.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.po_number + " " + (p.reference_no || "")).toLowerCase().includes(s);
  }), [pos, q, status]);

  return (
    <div className="space-y-4" data-testid="vendor-po-list">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Purchase Orders</h1>
        <p className="text-sm text-slate-500 mt-1">All Purchase Orders issued to your company by BAISHALII.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PO number or reference…" className="pl-9" data-testid="vendor-po-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="vendor-po-status" className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
          <option value="">All statuses</option>
          <option>Approved</option>
          <option>Sent</option>
          <option>Partially Received</option>
          <option>Closed</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">PO Number</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold text-right">PO Value</th>
              <th className="px-4 py-3 font-semibold text-right">Received</th>
              <th className="px-4 py-3 font-semibold text-right">Balance</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!pos && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {pos && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <ShoppingCart className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="font-semibold text-slate-700 mb-1">{q || status ? "No matching Purchase Orders" : "No Purchase Orders yet"}</p>
                  <p className="text-sm text-slate-500">{q || status ? "Try clearing filters." : "POs issued to your company will appear here."}</p>
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0a2a5e]">{p.po_number}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(p.po_date)}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(p.summary?.grand_total)}</td>
                <td className="px-4 py-3 text-right text-slate-600 whitespace-nowrap">₹ {inr(p.summary?.received_value)}</td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-700 whitespace-nowrap">₹ {inr(p.summary?.balance_value)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLR[p.status] || "bg-slate-100 text-slate-700"}`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/vendor/pos/${p.id}`} data-testid={`vendor-po-view-${p.id}`} className="text-[#0a2a5e] text-sm font-semibold hover:underline">View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
